import { useState, useEffect } from 'react';
import { getExploreLocationsV1 } from '@/services/platform/api/explore/explore';
import type { LocationDto } from '@/services/platform/api/model';

export interface ExploreLocationsState {
  locations: LocationDto[];
  isLoading: boolean;
}

/**
 * The last list the API returned during this page load — stale-while-revalidate.
 *
 * It is what a mount renders first, so returning to the map is instant, but every mount still asks
 * the network and replaces it. It is deliberately not persisted: a copy in `sessionStorage` lived as
 * long as the app process (days on iOS) and kept serving an old `mapImage` URL after the CMS changed.
 */
let memoryCache: LocationDto[] | null = null;

/**
 * The request currently in flight, shared by everyone who asks while it runs.
 *
 * `StrictMode` mounts every effect twice in development, and the map and the experience What sheet
 * can both be mounted at once — without this, one screen would issue several requests for one list.
 */
let inFlight: Promise<LocationDto[] | null> | null = null;

/**
 * Keeps the cached array when the response is unchanged, so consumers' `useMemo` deps only see a new
 * reference when the data actually changed.
 */
function remember(data: LocationDto[]): LocationDto[] {
  if (memoryCache && JSON.stringify(memoryCache) === JSON.stringify(data)) return memoryCache;
  memoryCache = data;
  return data;
}

/**
 * Resolves the fresh list, or `null` when the request failed — the caller then keeps whatever it
 * already shows rather than wiping a good list on a transient error.
 */
function revalidate(): Promise<LocationDto[] | null> {
  inFlight ??= getExploreLocationsV1()
    .then(response => (response.status === 200 ? remember(response.data) : null))
    .catch(() => null)
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

export function useExploreLocations(): ExploreLocationsState {
  // Lazy initialiser: the cache is read once per mount, not on every render.
  const [state, setState] = useState<ExploreLocationsState>(() => ({
    locations: memoryCache ?? [],
    isLoading: memoryCache === null,
  }));

  useEffect(() => {
    let cancelled = false;
    revalidate().then(fresh => {
      if (cancelled) return;
      setState(prev => ({ locations: fresh ?? prev.locations, isLoading: false }));
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
